import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  Key, Copy, Check, Trash2, Plus, Loader2,
  CreditCard, Shield, Globe, Code, AlertTriangle, RefreshCw,
  Terminal, ChevronDown, ChevronRight, Eye, EyeOff, Zap, Twitter, Lock
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/lib/api'

// ── Types ─────────────────────────────────────────────────────────────────────
interface ApiKey { masked: string; prefix: string }
interface UsageData {
  credits_total: number
  credits_used: number
  credits_remaining: number | null
  unlimited: boolean
  subscription_tier: string
  endpoint_costs: Record<string, Record<string, number>>
  api_keys_used: number
  api_keys_limit: number
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function getApiBaseUrl() {
  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return `${window.location.protocol}//${window.location.hostname}/api`
  }
  return (import.meta as any).env?.VITE_API_URL || 'http://localhost:8000'
}
const API_BASE = getApiBaseUrl()

const MODULE_META: Record<string, { label: string; icon: React.ElementType; color: string; border: string }> = {
  social_osint:    { label: 'Social OSINT',    icon: Globe,    color: 'text-blue-400',   border: 'border-blue-500/40' },
  x_investigation: { label: 'X Investigation', icon: Twitter,  color: 'text-sky-400',    border: 'border-sky-500/40' },
  breachint:       { label: 'BreachINT',        icon: Shield,   color: 'text-red-400',    border: 'border-red-500/40' },
}

function costBadge(cost: number) {
  if (cost >= 5) return 'bg-red-500/20 text-red-400 border-red-500/50'
  if (cost >= 3) return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50'
  return 'bg-green-500/20 text-green-400 border-green-500/50'
}

function tierColor(tier: string) {
  if (tier === 'enterprise')   return 'text-purple-400'
  if (tier === 'professional') return 'text-blue-400'
  if (tier === 'starter')      return 'text-cyan-400'
  if (tier === 'freemium')     return 'text-gray-300'
  return 'text-green-400'
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function DeveloperPortal() {
  useAuth()

  const [keys, setKeys]               = useState<ApiKey[]>([])
  const [usage, setUsage]             = useState<UsageData | null>(null)
  const [loadingKeys, setLoadingKeys] = useState(true)
  const [loadingUsage, setLoadingUsage] = useState(true)
  const [creating, setCreating]       = useState(false)
  const [revoking, setRevoking]       = useState<string | null>(null)

  const [newKey, setNewKey]           = useState<string | null>(null)   // shown once after creation
  const [copiedId, setCopiedId]       = useState<string | null>(null)
  const [showKey, setShowKey]         = useState(false)
  const [openModules, setOpenModules] = useState<Record<string, boolean>>({ breachint: true })
  const [error, setError]             = useState<string | null>(null)

  const copy = (text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const loadKeys = useCallback(async () => {
    setLoadingKeys(true)
    try {
      const res = await apiClient.developerListKeys()
      setKeys(res.keys)
    } catch (e: any) {
      setError('Failed to load API keys')
    } finally {
      setLoadingKeys(false)
    }
  }, [])

  const loadUsage = useCallback(async () => {
    setLoadingUsage(true)
    try {
      const res = await apiClient.developerUsage()
      setUsage(res)
    } catch (e: any) {
      setError('Failed to load usage data')
    } finally {
      setLoadingUsage(false)
    }
  }, [])

  useEffect(() => {
    loadKeys()
    loadUsage()
  }, [loadKeys, loadUsage])

  const handleCreateKey = async () => {
    setCreating(true)
    setError(null)
    try {
      const res = await apiClient.developerCreateKey()
      setNewKey(res.key)
      setShowKey(true)
      await loadKeys()
      await loadUsage()
    } catch (e: any) {
      setError(e.message || 'Failed to generate key')
    } finally {
      setCreating(false)
    }
  }

  const handleRevoke = async (prefix: string) => {
    if (!confirm('Revoke this API key? All requests using it will immediately stop working.')) return
    setRevoking(prefix)
    setError(null)
    try {
      await apiClient.developerRevokeKey(prefix)
      if (newKey && newKey.startsWith(prefix)) setNewKey(null)
      await loadKeys()
      await loadUsage()
    } catch (e: any) {
      setError(e.message || 'Failed to revoke key')
    } finally {
      setRevoking(null)
    }
  }

  // Credit percentage
  const creditPct = usage
    ? usage.unlimited
      ? 100
      : usage.credits_total > 0
        ? Math.max(0, Math.round(((usage.credits_remaining ?? 0) / usage.credits_total) * 100))
        : 0
    : 0

  const creditWarning = !usage?.unlimited && creditPct <= 20

  // Quick-start code uses the most recently generated key or placeholder
  const keyForCode = newKey ?? 'sk_your_api_key_here'
  const quickStart = {
    curl: `curl -X POST ${API_BASE}/v1/breach/search \\
  -H "X-API-Key: ${keyForCode}" \\
  -H "Content-Type: application/json" \\
  -d '{ "query": "user@example.com" }'`,
    python: `import requests

resp = requests.post(
    "${API_BASE}/v1/breach/search",
    headers={"X-API-Key": "${keyForCode}"},
    json={"query": "user@example.com"},
)
data = resp.json()
print(data["nodes"])          # breach graph nodes
print(data["_credits"])       # credits used / remaining`,
    js: `const res = await fetch('${API_BASE}/v1/breach/search', {
  method: 'POST',
  headers: {
    'X-API-Key': '${keyForCode}',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({ query: 'user@example.com' }),
})
const data = await res.json()
console.log(data._credits.credits_remaining)`,
  }

  const [codeTab, setCodeTab] = useState<'curl' | 'python' | 'js'>('curl')

  return (
    <div className="min-h-screen bg-[#0a0f0a] text-white">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(34,197,94,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(34,197,94,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 py-10 space-y-8">

        {/* ── Page header ── */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-400/20 to-emerald-600/20 border border-green-500/40 rounded-xl flex items-center justify-center">
                <Terminal className="w-5 h-5 text-green-400" />
              </div>
              Developer Portal
            </h1>
            <p className="text-gray-400 text-sm mt-1">Manage API keys, monitor credit usage, and explore endpoint pricing.</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { loadKeys(); loadUsage() }}
              className="text-gray-400 hover:text-green-400 border border-gray-700/50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Link to="/api-docs">
              <Button variant="outline" size="sm" className="border-green-500/40 text-green-400 hover:bg-green-500/10">
                <Code className="w-4 h-4 mr-2" />
                API Docs
              </Button>
            </Link>
          </div>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-4 flex items-center gap-3 text-red-400 text-sm">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto text-red-400/60 hover:text-red-400">✕</button>
          </div>
        )}

        {/* ── Row 1: Credit balance + Plan ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* Credit balance (2/3) */}
          <Card className="md:col-span-2 bg-black/50 border border-green-500/20 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-green-400 text-lg">
                <CreditCard className="w-5 h-5" />
                Credit Balance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingUsage ? (
                <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
              ) : usage ? (
                <div className="space-y-5">
                  {/* Big numbers */}
                  <div className="flex items-end gap-6 flex-wrap">
                    <div>
                      <div className={`text-5xl font-bold tabular-nums ${creditWarning ? 'text-red-400' : 'text-white'}`}>
                        {usage.unlimited ? '∞' : (usage.credits_remaining ?? 0).toLocaleString()}
                      </div>
                      <div className="text-gray-500 text-sm mt-1">credits remaining</div>
                    </div>
                    <div className="text-gray-600 text-2xl mb-1">/</div>
                    <div>
                      <div className="text-3xl font-semibold text-gray-300 tabular-nums">
                        {usage.unlimited ? '∞' : usage.credits_total.toLocaleString()}
                      </div>
                      <div className="text-gray-500 text-sm mt-1">total</div>
                    </div>
                    <div className="ml-auto text-right">
                      <div className="text-2xl font-bold text-gray-200 tabular-nums">{usage.credits_used.toLocaleString()}</div>
                      <div className="text-gray-500 text-sm mt-1">used this period</div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  {!usage.unlimited && (
                    <div>
                      <div className="h-2.5 bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            creditPct <= 10 ? 'bg-red-500' :
                            creditPct <= 20 ? 'bg-orange-500' :
                            creditPct <= 40 ? 'bg-yellow-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${creditPct}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>{creditPct}% remaining</span>
                        <span>{usage.credits_used} / {usage.credits_total} used</span>
                      </div>
                    </div>
                  )}

                  {/* Low credit warning */}
                  {creditWarning && (
                    <div className="bg-orange-500/10 border border-orange-500/40 rounded-lg p-3 flex items-center gap-2 text-orange-400 text-sm">
                      <AlertTriangle className="w-4 h-4 shrink-0" />
                      Credits running low. Upgrade your plan to avoid interruption.
                      <Link to="/app/subscription" className="ml-auto underline underline-offset-2 hover:text-orange-300 shrink-0">Upgrade</Link>
                    </div>
                  )}

                  <div className="text-xs text-gray-600">Credits reset on your billing cycle. Check <Link to="/app/subscription" className="text-green-500/70 hover:text-green-400">subscription page</Link> for next reset date.</div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No usage data available.</p>
              )}
            </CardContent>
          </Card>

          {/* Plan card (1/3) */}
          <Card className="bg-black/50 border border-gray-700/40 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-gray-300 text-lg flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                Your Plan
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {loadingUsage ? (
                <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
              ) : usage ? (
                <>
                  <div>
                    <div className={`text-3xl font-bold capitalize ${tierColor(usage.subscription_tier)}`}>
                      {usage.subscription_tier || 'Starter'}
                    </div>
                    <div className="text-gray-500 text-sm mt-1">subscription tier</div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-gray-400">
                      <span>API keys used</span>
                      <span className="text-white font-mono">{usage.api_keys_used} / {usage.api_keys_limit}</span>
                    </div>
                    <div className="flex justify-between text-gray-400">
                      <span>Total credits</span>
                      <span className="text-white font-mono">{usage.unlimited ? 'Unlimited' : usage.credits_total.toLocaleString()}</span>
                    </div>
                  </div>
                  <Link to="/app/subscription">
                    <Button variant="outline" size="sm" className="w-full border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10 mt-2">
                      Manage Plan
                    </Button>
                  </Link>
                </>
              ) : (
                <p className="text-gray-500 text-sm">No plan data available.</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ── Row 2: API Keys ── */}
        <Card className="bg-black/50 border border-yellow-500/20 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <CardTitle className="flex items-center gap-2 text-yellow-400 text-lg">
                  <Key className="w-5 h-5" />
                  API Keys
                </CardTitle>
                <CardDescription className="text-gray-500 text-sm mt-1">
                  Keys authenticate requests to <code className="text-green-400 text-xs">/v1/*</code> endpoints via the <code className="text-green-400 text-xs">X-API-Key</code> header. Maximum {usage?.api_keys_limit ?? 5} keys.
                </CardDescription>
              </div>
              {usage?.subscription_tier === 'freemium' ? (
                <Link to="/pricing">
                  <Button size="sm" className="bg-green-500 hover:bg-green-600 text-black font-semibold">
                    <Zap className="w-4 h-4 mr-2" />
                    Upgrade to Generate Keys
                  </Button>
                </Link>
              ) : (
                <Button
                  onClick={handleCreateKey}
                  disabled={creating || (!!usage && keys.length >= usage.api_keys_limit)}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold disabled:opacity-50"
                  size="sm"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Generate Key
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* Starter tier gate */}
            {!loadingUsage && usage?.subscription_tier === 'freemium' && (
              <div className="bg-yellow-500/5 border border-yellow-500/30 rounded-xl p-6 text-center space-y-3">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-yellow-500/10 mb-1">
                  <Lock className="w-6 h-6 text-yellow-400" />
                </div>
                <div className="text-yellow-400 font-semibold">API Access Not Included</div>
                <p className="text-gray-400 text-sm max-w-sm mx-auto">
                  API key generation and programmatic access to <code className="text-green-400 text-xs">/v1/*</code> endpoints require a paid plan.
                </p>
                <Link to="/pricing">
                  <Button className="bg-green-500 hover:bg-green-600 text-black font-semibold mt-2">
                    <Zap className="w-4 h-4 mr-2" />
                    View Plans & Upgrade
                  </Button>
                </Link>
              </div>
            )}

            {/* One-time new key reveal */}
            {usage?.subscription_tier !== 'freemium' && newKey && (
              <div className="bg-green-500/10 border border-green-500/40 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2 text-green-400 text-sm font-semibold">
                  <Check className="w-4 h-4" />
                  New API key generated — copy it now, it will not be shown again.
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-black/60 border border-green-500/30 rounded-lg px-4 py-2.5 font-mono text-sm text-green-300 flex items-center gap-2 overflow-x-auto">
                    {showKey ? newKey : `${newKey.slice(0, 12)}${'•'.repeat(24)}${newKey.slice(-4)}`}
                  </div>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => setShowKey(v => !v)}
                    className="text-gray-400 hover:text-green-400 shrink-0"
                  >
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => copy(newKey, 'new-key')}
                    className="text-gray-400 hover:text-green-400 shrink-0"
                  >
                    {copiedId === 'new-key' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => setNewKey(null)}
                    className="text-gray-500 hover:text-gray-300 shrink-0 text-xs"
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            )}

            {/* Key list — hidden for starter tier */}
            {usage?.subscription_tier !== 'freemium' && (
            <>
            {loadingKeys ? (
              <div className="flex items-center gap-2 text-gray-500 text-sm py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading keys…</div>
            ) : keys.length === 0 ? (
              <div className="text-center py-10 text-gray-600 border border-dashed border-gray-700/50 rounded-xl">
                <Key className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No API keys yet. Generate one to get started.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {keys.map((k) => (
                  <div
                    key={k.prefix}
                    className="flex items-center gap-3 bg-black/40 border border-gray-700/40 rounded-lg px-4 py-3 hover:border-gray-600/60 transition-colors"
                  >
                    <Key className="w-4 h-4 text-yellow-400/70 shrink-0" />
                    <code className="flex-1 font-mono text-sm text-gray-300">{k.masked}</code>
                    <Badge variant="outline" className="text-gray-500 border-gray-600/50 text-xs font-mono">{k.prefix}</Badge>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => copy(k.prefix, `copy-${k.prefix}`)}
                      className="text-gray-500 hover:text-yellow-400 h-7 w-7 p-0"
                      title="Copy prefix"
                    >
                      {copiedId === `copy-${k.prefix}` ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => handleRevoke(k.prefix)}
                      disabled={revoking === k.prefix}
                      className="text-gray-500 hover:text-red-400 h-7 w-7 p-0"
                      title="Revoke key"
                    >
                      {revoking === k.prefix ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                ))}
              </div>
            )}
            </>
            )}

            <p className="text-xs text-gray-600">
              Keys are rate-limited per tier. Usage: {usage ? `${keys.length} / ${usage.api_keys_limit}` : '—'} keys.
            </p>
          </CardContent>
        </Card>

        {/* ── Row 3: Pricing + Quick Start ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Endpoint pricing */}
          <Card className="bg-black/50 border border-purple-500/20 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-purple-400 text-lg">
                <CreditCard className="w-5 h-5" />
                Endpoint Pricing
              </CardTitle>
              <CardDescription className="text-gray-500 text-sm">Credits deducted at call time. HTTP 402 if insufficient.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {loadingUsage ? (
                <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
              ) : usage?.endpoint_costs ? (
                Object.entries(usage.endpoint_costs).map(([moduleKey, endpoints]) => {
                  const meta = MODULE_META[moduleKey] ?? { label: moduleKey, icon: Code, color: 'text-gray-400', border: 'border-gray-600/40' }
                  const Icon = meta.icon
                  return (
                    <div key={moduleKey} className={`border ${meta.border} rounded-xl overflow-hidden`}>
                      <button
                        onClick={() => setOpenModules(p => ({ ...p, [moduleKey]: !p[moduleKey] }))}
                        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-white/5 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${meta.color}`} />
                          <span className={`font-semibold text-sm ${meta.color}`}>{meta.label}</span>
                          <Badge variant="outline" className="text-xs border-gray-600/50 text-gray-500">{Object.keys(endpoints).length} endpoints</Badge>
                        </div>
                        {openModules[moduleKey]
                          ? <ChevronDown className="w-4 h-4 text-gray-500" />
                          : <ChevronRight className="w-4 h-4 text-gray-500" />
                        }
                      </button>
                      {openModules[moduleKey] && (
                        <div className="border-t border-gray-800/60 divide-y divide-gray-800/40">
                          {Object.entries(endpoints).map(([ep, cost]) => (
                            <div key={ep} className="flex items-center justify-between px-4 py-2 hover:bg-white/5">
                              <code className="text-xs text-gray-400 font-mono">{ep}</code>
                              <Badge className={`${costBadge(cost)} text-xs shrink-0 ml-3`}>{cost} cr</Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })
              ) : (
                <p className="text-gray-500 text-sm">No pricing data available.</p>
              )}
            </CardContent>
          </Card>

          {/* Quick start */}
          <Card className="bg-black/50 border border-green-500/20 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-green-400 text-lg">
                <Terminal className="w-5 h-5" />
                Quick Start
              </CardTitle>
              <CardDescription className="text-gray-500 text-sm">
                Base URL: <code className="text-green-400 text-xs">{API_BASE}</code>
                {newKey && <span className="ml-2 text-green-500/70 text-xs">— your new key is pre-filled below</span>}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Tab bar */}
              <div className="flex gap-1 bg-black/40 border border-gray-700/40 rounded-lg p-1">
                {(['curl', 'python', 'js'] as const).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setCodeTab(tab)}
                    className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      codeTab === tab
                        ? 'bg-green-500/20 text-green-400'
                        : 'text-gray-500 hover:text-gray-300'
                    }`}
                  >
                    {tab === 'js' ? 'JavaScript' : tab === 'curl' ? 'cURL' : 'Python'}
                  </button>
                ))}
              </div>

              {/* Code block */}
              <div className="relative bg-black/60 border border-green-500/20 rounded-xl p-4 font-mono text-xs overflow-x-auto">
                <Button
                  variant="ghost" size="sm"
                  onClick={() => copy(quickStart[codeTab], `qs-${codeTab}`)}
                  className="absolute top-2 right-2 h-7 w-7 p-0 text-gray-500 hover:text-green-400"
                >
                  {copiedId === `qs-${codeTab}` ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                </Button>
                <pre className="text-gray-300 leading-relaxed whitespace-pre-wrap break-all">
                  <code>{quickStart[codeTab]}</code>
                </pre>
              </div>

              {/* Response shape */}
              <div>
                <p className="text-xs text-gray-500 mb-2">Every response includes a <code className="text-green-400">_credits</code> object:</p>
                <div className="bg-black/60 border border-gray-700/40 rounded-lg p-3 font-mono text-xs text-gray-400">
                  <pre>{`"_credits": {
  "credits_used": 3,
  "credits_remaining": 47,
  "credits_total": 50
}`}</pre>
                </div>
              </div>

              {/* Links */}
              <div className="flex gap-3 pt-2">
                <Link to="/api-docs" className="flex-1">
                  <Button variant="outline" size="sm" className="w-full border-green-500/30 text-green-400 hover:bg-green-500/10 text-xs">
                    Full API Reference
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>

      </div>
    </div>
  )
}
